import { Checkbox, ListItemSecondaryAction, ListItemText, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import React from "react";
import { ExamTemplateWithStructureIndex } from "../../model/Exam";
import { GetTranslatedValue } from "../../model/Shared/TranslatedValue";
import CustomSearchBox from "../common/customSearchBox/CustomSearchBox";
import TranslatedMessage from "../translations/TranslatedMessage";

type PublishedExamSelectionDialogProps = {
  isOpened: boolean;
  handleAccept: (selection: ExamTemplateWithStructureIndex[]) => void;
  handleCancel: () => void;
  selectedExams?: ExamTemplateWithStructureIndex[];
  selectableExams: ExamTemplateWithStructureIndex[];
};

const PublishedExamSelectionDialog = (props: PublishedExamSelectionDialogProps) => {
  const { isOpened, handleAccept, handleCancel, selectableExams } = props;
  const [selection, setSelection] = React.useState<string[]>([]);
  const [searchToken, setSearchToken] = React.useState<string>("");
  const [filteredExams, setFilteredExams] = React.useState<ExamTemplateWithStructureIndex[]>(selectableExams);

  React.useEffect(() => {
    setFilteredExams(selectableExams)
  }, [selectableExams])

  const onAccept = () => {
    const selectedExams = selectableExams.filter((exam) => selection.indexOf(exam.id) !== -1);
    handleAccept(selectedExams);
    setSelection([]);
    setSearchToken("");
  };

  const onCancel = () => {
    handleCancel();
    setSelection([]);
    setSearchToken("");
  };

  const onSearchTokenChanged = (token: string) => {
    const nonSelectedFilteredExams = selectableExams.filter((exam) => selection.indexOf(exam.id) === -1 
      && (GetTranslatedValue(exam.name).toLowerCase().indexOf(token.toLowerCase()) !== -1
        || GetTranslatedValue(exam.includedInCourses).toLowerCase().indexOf(token.toLowerCase()) !== -1
        || GetTranslatedValue(exam.includedInModules).toLowerCase().indexOf(token.toLowerCase()) !== -1)
    );

    setFilteredExams(nonSelectedFilteredExams);
    setSearchToken(token);
  };

  const toggleExamSelection = (exam: ExamTemplateWithStructureIndex) => {
    if (selection.indexOf(exam.id) === -1) {
      setSelection([...selection, exam.id]);
    } else {
      setSelection(selection.filter((id) => id !== exam.id));
    }
  };

  const getExamSecondaryText = (exam: ExamTemplateWithStructureIndex): React.ReactNode => {
    return <div>
      <ul>
        <li><TranslatedMessage id="course.list.header" />: {GetTranslatedValue(exam.includedInCourses)}</li>
        <li><TranslatedMessage id="module.header" />: {GetTranslatedValue(exam.includedInModules)}</li>
      </ul>
    </div>;
  }

  const isListEmpty = filteredExams.length === 0;

  return (
    <Dialog onClose={handleCancel} open={isOpened} maxWidth={"sm"} fullWidth={true}>
      <DialogTitle><TranslatedMessage id="module.dialog.selectExams" /></DialogTitle>
      <DialogContent>
        <CustomSearchBox onChange={(event: any) => onSearchTokenChanged(event.target.value)} searchToken={searchToken} />

        {isListEmpty ? 
          <Typography style={emptyMessage}><TranslatedMessage id="module.dialog.noAvailableExams" /></Typography>
        :
          <List style={listContainer}>
            {filteredExams.map((exam) => (
              <ListItem button onClick={() => toggleExamSelection(exam)} key={exam.id}>
                <ListItemText primary={GetTranslatedValue(exam.name)} secondary={getExamSecondaryText(exam)} />
                <ListItemSecondaryAction>
                  <Checkbox edge="end" onChange={() => toggleExamSelection(exam)} checked={selection.indexOf(exam.id) !== -1} />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} color="primary">
          <TranslatedMessage id="common.ok" />
        </Button>
        <Button onClick={onCancel} color="primary">
          <TranslatedMessage id="common.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const listContainer: React.CSSProperties = {
  height: 400,
  overflow: "auto",
  marginTop: 10,
  paddingTop: 0
};

const emptyMessage: React.CSSProperties = {
  height: 400,
  marginTop: 10,
  paddingTop: 200,
  textAlign: "center"
};

export default PublishedExamSelectionDialog;
